import React from "react";
import { ArrowUpCircle, Gavel, AlertTriangle } from "lucide-react";

// Phase 12 — minimal status pill
//   default form  → 6px colored dot + plain text in --page-fg (low visual noise)
//   emphasis form → tinted background + colored text (only for high-emphasis statuses)

const DOT_COLOR = {
  New: "#3B82F6",                      // blue
  "In Progress": "#0EA5A4",            // teal
  "Under Investigation": "#8B5CF6",    // purple
  "Pending Review": "#64748B",         // slate
  "Pending Follow-up": "#E12AFB",      // magenta
  Escalated: "#E5484D",                // red
  Closed: "#5EAB45",                   // brand green
  Open: "#3B82F6",                     // legacy alias
};

const EMPHASIS_TINT = {
  // Phase 12 brief — only Escalated, High-Risk, Legal Hold, Pending Review get full chips
  Escalated: {
    bg: "var(--danger-bg)",
    fg: "var(--danger-fg)",
    border: "var(--danger-border)",
    Icon: ArrowUpCircle,
  },
  "Pending Review": {
    bg: "var(--surface-2)",
    fg: "var(--page-fg-secondary)",
    border: "var(--border-strong)",
  },
};

export const STATUS_VALUES = [
  "New",
  "In Progress",
  "Under Investigation",
  "Pending Review",
  "Pending Follow-up",
  "Escalated",
  "Closed",
];

export function StatusPill({ status, className = "", variant = "auto" }) {
  const isEmphasis =
    variant === "emphasis" || (variant === "auto" && EMPHASIS_TINT[status]);
  const dot = DOT_COLOR[status] || "#A1A1AA";
  const tid = `status-pill-${(status || "unknown").toLowerCase().replace(/\s+/g, "-")}`;

  if (isEmphasis) {
    const t = EMPHASIS_TINT[status] || { bg: "transparent", fg: "var(--page-fg)", border: "var(--border-strong)" };
    const Icon = t.Icon;
    return (
      <span
        data-testid={tid}
        className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] font-medium tabular-nums ${className}`}
        style={{ backgroundColor: t.bg, color: t.fg, borderColor: t.border }}
      >
        {Icon ? (
          <Icon className="h-3 w-3" strokeWidth={2} />
        ) : (
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: dot }}
          />
        )}
        {status}
      </span>
    );
  }

  // Default minimal-glyph form: tiny colored dot + text in primary
  return (
    <span
      data-testid={tid}
      className={`inline-flex items-center gap-1.5 text-[11px] font-medium tabular-nums ${className}`}
      style={{ color: "var(--page-fg)" }}
    >
      <span
        aria-hidden
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: dot }}
      />
      {status}
    </span>
  );
}

// Phase 12 — emphasis chips for high-risk + legal hold (used outside StatusPill)
export function HighRiskChip({ className = "" }) {
  return (
    <span
      data-testid="high-risk-chip"
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${className}`}
      style={{
        backgroundColor: "var(--danger-bg)",
        color: "var(--danger-fg)",
        borderColor: "var(--danger-border)",
      }}
    >
      <AlertTriangle className="h-3 w-3" strokeWidth={2} />
      High-risk
    </span>
  );
}

export function LegalHoldChip({ className = "" }) {
  return (
    <span
      data-testid="legal-hold-chip"
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${className}`}
      style={{
        backgroundColor: "var(--danger-bg)",
        color: "var(--danger-fg)",
        borderColor: "var(--danger-border)",
      }}
    >
      <Gavel className="h-3 w-3" strokeWidth={2} />
      Legal hold
    </span>
  );
}

export default StatusPill;
